"use client"

import { useEffect, useState } from 'react'
import { usePostHog } from 'posthog-js/react'
import Link from 'next/link'
import Image from 'next/image'
import { LocalShareButtons } from '@/components/blocks/local-share-buttons'
import { Button, buttonVariants } from "@/components/ui/button"
import { Input } from "@/components/ui/input"
import { ChevronRight, MapPin, Loader2 } from 'lucide-react'
import { createClient } from "@/lib/supabase/client"
import { STATE_SLUGS } from "@/lib/state-utils"
import { stateFlagStyle } from '@/lib/state-flag-sizes'

// Adjacent states (by region)
const ADJACENT_STATES: Record<string, string[]> = {
  // Northeast
  'ME': ['NH', 'VT', 'MA'], 'NH': ['ME', 'VT', 'MA'], 'VT': ['NH', 'NY', 'MA'],
  'MA': ['NH', 'CT', 'RI', 'NY'], 'RI': ['MA', 'CT'], 'CT': ['MA', 'NY', 'RI'],
  'NY': ['NJ', 'PA', 'CT', 'VT', 'MA'], 'NJ': ['NY', 'PA', 'DE'],
  'PA': ['NY', 'NJ', 'DE', 'MD', 'OH', 'WV'],
  // Southeast
  'DE': ['MD', 'PA', 'NJ'], 'MD': ['PA', 'DE', 'VA', 'WV', 'DC'],
  'VA': ['MD', 'WV', 'NC', 'TN', 'KY'], 'WV': ['PA', 'MD', 'VA', 'OH', 'KY'],
  'NC': ['VA', 'SC', 'GA', 'TN'], 'SC': ['NC', 'GA'],
  'GA': ['SC', 'FL', 'AL', 'TN', 'NC'], 'FL': ['GA', 'AL'],
  // South Central
  'AL': ['GA', 'FL', 'MS', 'TN'], 'MS': ['AL', 'TN', 'AR', 'LA'],
  'TN': ['KY', 'VA', 'NC', 'GA', 'AL', 'MS', 'AR', 'MO'],
  'KY': ['OH', 'WV', 'VA', 'TN', 'MO', 'IL', 'IN'],
  'LA': ['MS', 'AR', 'TX'], 'AR': ['MO', 'TN', 'MS', 'LA', 'TX', 'OK'],
  // Southwest
  'TX': ['LA', 'AR', 'OK', 'NM'], 'OK': ['KS', 'MO', 'AR', 'TX', 'NM', 'CO'],
  'NM': ['TX', 'OK', 'CO', 'AZ', 'UT'], 'AZ': ['NM', 'UT', 'NV', 'CA'],
  // West
  'CA': ['OR', 'NV', 'AZ'], 'NV': ['CA', 'OR', 'ID', 'UT', 'AZ'],
  'OR': ['WA', 'CA', 'NV', 'ID'], 'WA': ['OR', 'ID'],
  'ID': ['WA', 'OR', 'NV', 'UT', 'WY', 'MT'],
  'MT': ['ID', 'WY', 'ND', 'SD'], 'WY': ['MT', 'ID', 'UT', 'CO', 'NE', 'SD'],
  'UT': ['ID', 'WY', 'CO', 'NM', 'AZ', 'NV'], 'CO': ['WY', 'NE', 'KS', 'OK', 'NM', 'UT'],
  // Midwest
  'ND': ['MT', 'SD', 'MN'], 'SD': ['ND', 'MT', 'WY', 'NE', 'IA', 'MN'],
  'NE': ['SD', 'WY', 'CO', 'KS', 'MO', 'IA'], 'KS': ['NE', 'CO', 'OK', 'MO'],
  'MN': ['ND', 'SD', 'IA', 'WI'], 'IA': ['MN', 'SD', 'NE', 'MO', 'IL', 'WI'],
  'MO': ['IA', 'NE', 'KS', 'OK', 'AR', 'TN', 'KY', 'IL'],
  'WI': ['MN', 'IA', 'IL', 'MI'], 'IL': ['WI', 'IA', 'MO', 'KY', 'IN', 'MI'],
  'MI': ['WI', 'IL', 'IN', 'OH'], 'IN': ['MI', 'IL', 'KY', 'OH'],
  'OH': ['MI', 'IN', 'KY', 'WV', 'PA'],
  // Non-contiguous
  'AK': [], 'HI': [],
}

const STATE_NAMES: Record<string, string> = {
  'AL': 'Alabama', 'AK': 'Alaska', 'AZ': 'Arizona', 'AR': 'Arkansas', 'CA': 'California',
  'CO': 'Colorado', 'CT': 'Connecticut', 'DE': 'Delaware', 'FL': 'Florida', 'GA': 'Georgia',
  'HI': 'Hawaii', 'ID': 'Idaho', 'IL': 'Illinois', 'IN': 'Indiana', 'IA': 'Iowa',
  'KS': 'Kansas', 'KY': 'Kentucky', 'LA': 'Louisiana', 'ME': 'Maine', 'MD': 'Maryland',
  'MA': 'Massachusetts', 'MI': 'Michigan', 'MN': 'Minnesota', 'MS': 'Mississippi', 'MO': 'Missouri',
  'MT': 'Montana', 'NE': 'Nebraska', 'NV': 'Nevada', 'NH': 'New Hampshire', 'NJ': 'New Jersey',
  'NM': 'New Mexico', 'NY': 'New York', 'NC': 'North Carolina', 'ND': 'North Dakota', 'OH': 'Ohio',
  'OK': 'Oklahoma', 'OR': 'Oregon', 'PA': 'Pennsylvania', 'RI': 'Rhode Island', 'SC': 'South Carolina',
  'SD': 'South Dakota', 'TN': 'Tennessee', 'TX': 'Texas', 'UT': 'Utah', 'VT': 'Vermont',
  'VA': 'Virginia', 'WA': 'Washington', 'WV': 'West Virginia', 'WI': 'Wisconsin', 'WY': 'Wyoming'
}

interface StatePageClientProps {
  stateName: string
  stateCode: string
  electricityRate: number
  citiesCount: number
}

export function StatePageClient({
  stateName,
  stateCode,
  electricityRate,
  citiesCount
}: StatePageClientProps) {
  const posthog = usePostHog()

  // Track page view
  useEffect(() => {
    if (posthog) {
      posthog.capture('local_state_viewed', {
        state_code: stateCode,
        state_name: stateName,
        electricity_rate: electricityRate,
        cities_count: citiesCount
      })
    }
  }, [posthog, stateCode, stateName, electricityRate, citiesCount])

  const adjacentStateCodes = ADJACENT_STATES[stateCode] || []
  const adjacentStates = adjacentStateCodes
    .filter(code => STATE_NAMES[code])
    .slice(0, 5)
    .map(code => ({
      code,
      name: STATE_NAMES[code],
      slug: STATE_SLUGS[code] || (code || '').toLowerCase()
    }))

  return (
    <>
      {/* Share Buttons - Top */}
      <div className="mb-6">
        <LocalShareButtons
          locationName={stateName}
          variant="top"
          electricityRate={electricityRate}
        />
      </div>

      {/* Adjacent States Navigation */}
      {adjacentStates.length > 0 && (
        <div className="mb-6 p-4 bg-gray-50 rounded-lg border border-gray-100">
          <p className="text-sm text-gray-600 mb-2">
            Nearby States:
          </p>
          <div className="flex flex-wrap gap-2">
            {adjacentStates.map((st) => (
              <Link
                key={st.code}
                href={`/local/${st.slug}`}
                className="inline-flex items-center gap-2 px-3 py-1.5 bg-white border border-[#ff9951]/30 rounded-full text-sm text-[#ff9951] hover:border-[#ff9951] hover:bg-[#ff9951]/5 transition-all"
              >
                <Image
                  src={`/state-flags/fridge.com-${(st.code || '').toLowerCase()}-flag.png`}
                  style={stateFlagStyle(st.code)}
                  alt={`Flag of ${st.name}`}
                  width={28}
                  height={18}
                  className="rounded-sm border border-gray-200"
                />
                {st.name}
              </Link>
            ))}
            <Link
              href="/local"
              className="inline-flex items-center gap-1 px-3 py-1.5 bg-[#f2f7ff] border border-[#0469f0]/20 rounded-full text-sm text-[#0469f0] hover:border-[#0469f0] transition-colors"
            >
              View All 50 States
              <ChevronRight className="w-3 h-3" />
            </Link>
          </div>
        </div>
      )}
    </>
  )
}

// Bottom share section - to be placed at end of page content
export function StatePageShareBottom({
  stateName,
  electricityRate
}: {
  stateName: string
  electricityRate: number
}) {
  return (
    <div className="mt-8 pt-6 pb-8 border-t border-gray-200">
      <LocalShareButtons
        locationName={stateName}
        variant="bottom"
        electricityRate={electricityRate}
      />
    </div>
  )
}

// ZIP preview data interface
interface ZipPreviewData {
  zip: string
  city_slug: string
  city_name: string
  state_code: string
  rate: number
  utility_name: string | null
}

// ZIP Code lookup component for state pages - with preview for better SEO
export function StateZipLookup({
  stateCode,
  stateName,
  zipCount,
  avgZipRate
}: {
  stateCode: string
  stateName: string
  zipCount: number
  avgZipRate: number | null
}) {
  const posthog = usePostHog()
  const [zipCode, setZipCode] = useState('')
  const [error, setError] = useState('')
  const [isLoading, setIsLoading] = useState(false)
  const [previewData, setPreviewData] = useState<ZipPreviewData | null>(null)

  const handleZipLookup = async () => {
    setError('')
    setPreviewData(null)
    setIsLoading(true)
    
    const cleanZip = zipCode.replace(/\D/g, '')
    if (cleanZip.length !== 5) {
      setError('Please enter a valid 5-digit zip code')
      setIsLoading(false)
      return
    }

    try {
      const supabase = createClient()
      const { data: zipData, error: dbError } = await supabase
        .from('zip_energy_data')
        .select('zip, city_slug, city_name, state_code, rate, utility_name')
        .eq('zip', cleanZip)
        .single()

      if (dbError || !zipData) {
        setError(`ZIP ${cleanZip} not found. Try another ZIP code in ${stateName}.`)
        setIsLoading(false)
        return
      }

      // Track ZIP lookup (preview shown)
      if (posthog) {
        posthog.capture('zip_lookup', {
          source_page: 'state',
          source_state: stateCode,
          searched_zip: cleanZip,
          found_city: zipData.city_slug,
          found_state: zipData.state_code,
          preview_shown: true
        })
      }

      // Show preview instead of navigating
      setPreviewData(zipData as ZipPreviewData)
      setIsLoading(false)
    } catch {
      setError('Could not look up ZIP code. Please try again.')
      setIsLoading(false)
    }
  }

  const handleViewDetails = () => {
    if (!previewData) return
    
    // Track click-through from preview
    if (posthog) {
      posthog.capture('zip_preview_click', {
        source_page: 'state',
        source_state: stateCode,
        zip: previewData.zip,
        city: previewData.city_slug
      })
    }
  }

  const clearPreview = () => {
    setPreviewData(null)
    setZipCode('')
  }

  if (zipCount === 0) return null

  return (
    <div className="mb-8 p-4 bg-gray-50 border border-gray-100 rounded-xl">
      <div className="flex flex-col gap-3">
        <div>
          <p className="font-semibold text-gray-900">
            {zipCount.toLocaleString()} ZIP Codes with Utility-Rate Estimates
          </p>
          <p className="text-sm text-gray-600">
            {avgZipRate ? `Mean stored estimate: ${avgZipRate.toFixed(1)}¢/kWh across ${stateName}; your tariff may differ.` : `Utility-average estimates for ${stateName}; your tariff may differ.`}
          </p>
        </div>
        
        {/* Search Input */}
        <div className="flex gap-2 max-w-md">
          <Input
            type="text"
            name="zip"
            autoComplete="postal-code"
            inputMode="numeric"
            aria-label="ZIP code"
            placeholder="Enter ZIP code"
            value={zipCode}
            onChange={(e) => setZipCode(e.target.value)}
            onKeyDown={(e) => e.key === 'Enter' && handleZipLookup()}
            className="flex-1"
            maxLength={5}
          />
          <Button onClick={handleZipLookup} variant="outline" size="default" disabled={isLoading}>
            {isLoading ? (
              <Loader2 className="h-4 w-4 mr-1 animate-spin" />
            ) : (
              <MapPin className="h-4 w-4 mr-1" />
            )}
            {isLoading ? 'Finding...' : 'Find'}
          </Button>
        </div>
        
        {error && (
          <p className="text-red-600 text-sm">{error}</p>
        )}
        
        {/* Preview Card */}
        {previewData && (
          <div className="mt-2 p-4 bg-white rounded-lg border border-[#0469f0]/30 shadow-sm">
            <div className="flex items-start justify-between gap-4">
              <div className="flex-1">
                <p className="text-lg font-semibold text-gray-900">
                  {previewData.city_name}, {previewData.state_code} {previewData.zip}
                </p>
                <div className="mt-2 grid grid-cols-2 gap-3">
                  <div>
                    <p className="text-xs text-gray-500 uppercase tracking-wide">Electricity Rate</p>
                    <p className="text-xl font-bold text-[#0469f0]">{previewData.rate.toFixed(1)}¢/kWh</p>
                  </div>
                  {previewData.utility_name && (
                    <div>
                      <p className="text-xs text-gray-500 uppercase tracking-wide">Utility Provider</p>
                      <p className="text-sm font-medium text-gray-900">{previewData.utility_name}</p>
                    </div>
                  )}
                </div>
              </div>
            </div>
            <div className="mt-4 flex gap-2">
              <Link
                href={`/local/${STATE_SLUGS[previewData.state_code]}/${previewData.city_slug}/${previewData.zip}`}
                onClick={handleViewDetails}
                className={buttonVariants({ variant: "ctaPrimary", className: "w-full flex-1" })}
              >
                View Full Details
                <ChevronRight className="h-4 w-4 ml-1" />
              </Link>
              <Button variant="outline" size="default" onClick={clearPreview}>
                Search Another
              </Button>
            </div>
          </div>
        )}
      </div>
    </div>
  )
}
